import { downloadFile } from '../../utils/api';
import React, { useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { Spinner, ErrorBanner, EmptyState } from '../../components/ui';
import { fmt } from '../../utils/format';

function ReportCard({ title, icon, children, exportUrl, children2 }) {
  return (
    <div className="card" style={{ marginBottom:12 }}>
      <div className="card-title" style={{ display:'flex', justifyContent:'space-between' }}>
        <span><i className={`ti ${icon}`} style={{ marginRight:6 }} />{title}</span>
        {exportUrl && <button className="btn btn-sm" onClick={() => downloadFile(exportUrl)}><i className="ti ti-download" /> Export CSV</button>}
      </div>
      {children}
    </div>
  );
}

export function InventoryValuation() {
  const { data, loading, error } = useFetch('/api/reports/inventory-valuation');
  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;
  if (!data) return null;
  const { products, materials, totals } = data;
  return (
    <div>
      <div className="page-header"><h1>Inventory Valuation</h1></div>
      <div className="metrics-grid" style={{ marginBottom:16 }}>
        <div className="metric-card"><div className="metric-label">Warehouse (finished goods)</div><div className="metric-value">{fmt.currency(totals.finishedGoodsWarehouse)}</div></div>
        <div className="metric-card"><div className="metric-label">Amazon FBA (finished goods)</div><div className="metric-value">{fmt.currency(totals.finishedGoodsAmazon)}</div></div>
        <div className="metric-card"><div className="metric-label">Raw materials</div><div className="metric-value">{fmt.currency(totals.rawMaterials)}</div></div>
        <div className="metric-card"><div className="metric-label">Grand total</div><div className="metric-value" style={{ color:'var(--blue)' }}>{fmt.currency(totals.grandTotal)}</div></div>
      </div>
      <ReportCard title="Finished Goods" icon="ti-package" exportUrl="/api/reports/inventory-valuation/csv">
        <div className="table-wrap"><table>
          <thead><tr><th>Product</th><th>SKU</th><th>Warehouse qty</th><th>Amazon qty</th><th>Unit cost</th><th>Warehouse value</th><th>Amazon value</th><th>Total value</th></tr></thead>
          <tbody>{products.map((p,i) => <tr key={i}><td>{p.name}</td><td style={{ fontFamily:'monospace', fontSize:12 }}>{p.sku}</td><td>{p.quantity_on_hand}</td><td>{p.quantity_at_amazon}</td><td>{fmt.currency(p.unit_cost)}</td><td>{fmt.currency(p.warehouse_value)}</td><td>{fmt.currency(p.amazon_value)}</td><td style={{ fontWeight:500 }}>{fmt.currency(p.total_value)}</td></tr>)}</tbody>
        </table></div>
      </ReportCard>
      <ReportCard title="Raw Materials" icon="ti-box">
        <div className="table-wrap"><table>
          <thead><tr><th>Material</th><th>On hand</th><th>UoM</th><th>Unit cost</th><th>Total value</th></tr></thead>
          <tbody>{materials.map((m,i) => <tr key={i}><td>{m.name}</td><td>{m.quantity_on_hand}</td><td>{m.unit_of_measure}</td><td>{fmt.currency(m.unit_cost)}</td><td style={{ fontWeight:500 }}>{fmt.currency(m.total_value)}</td></tr>)}</tbody>
        </table></div>
      </ReportCard>
    </div>
  );
}

export function GrossMargin() {
  const { data, loading, error } = useFetch('/api/reports/gross-margin');
  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;
  return (
    <div>
      <div className="page-header"><h1>Gross Margin</h1></div>
      <div className="card">
        <div className="table-wrap"><table>
          <thead><tr><th>Product</th><th>SKU</th><th>Sell price</th><th>Material cost</th><th>Gross profit</th><th>Margin %</th></tr></thead>
          <tbody>{(data?.products||[]).map((p,i) => (
            <tr key={i}>
              <td>{p.name}</td>
              <td style={{ fontFamily:'monospace', fontSize:12 }}>{p.sku}</td>
              <td>{fmt.currency(p.unit_price)}</td>
              <td>{fmt.currency(p.bom_cost)}</td>
              <td>{fmt.currency(p.gross_profit)}</td>
              <td><span className={`badge ${p.gross_margin_pct>=50?'badge-ok':p.gross_margin_pct>=20?'badge-amber':'badge-crit'}`}>{fmt.pct(p.gross_margin_pct)}</span></td>
            </tr>
          ))}</tbody>
        </table></div>
      </div>
    </div>
  );
}

export function Shrinkage() {
  const { data, loading, error } = useFetch('/api/reports/shrinkage');
  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;
  return (
    <div>
      <div className="page-header"><h1>Shrinkage Report</h1><div className="page-actions"><span style={{ fontSize:12, color:'var(--text-tertiary)' }}>Last 90 days — manual negative adjustments</span></div></div>
      <ReportCard title="Finished Goods Shrinkage" icon="ti-package">
        <div className="table-wrap"><table>
          <thead><tr><th>Product</th><th>SKU</th><th>Units lost</th><th>Value lost</th></tr></thead>
          <tbody>{(data?.products||[]).map((p,i) => <tr key={i}><td>{p.name}</td><td style={{ fontFamily:'monospace', fontSize:12 }}>{p.sku}</td><td style={{ color:'var(--red)' }}>{p.shrinkage_units}</td><td style={{ color:'var(--red)' }}>{fmt.currency(Math.abs(p.shrinkage_value))}</td></tr>)}</tbody>
        </table>{!(data?.products||[]).length && <EmptyState icon="ti-chart-area-line" message="No shrinkage recorded" />}</div>
      </ReportCard>
      <ReportCard title="Raw Material Shrinkage" icon="ti-box">
        <div className="table-wrap"><table>
          <thead><tr><th>Material</th><th>Units lost</th><th>Value lost</th></tr></thead>
          <tbody>{(data?.materials||[]).map((m,i) => <tr key={i}><td>{m.name}</td><td style={{ color:'var(--red)' }}>{m.shrinkage_units} {m.unit_of_measure}</td><td style={{ color:'var(--red)' }}>{fmt.currency(Math.abs(m.shrinkage_value))}</td></tr>)}</tbody>
        </table>{!(data?.materials||[]).length && <EmptyState icon="ti-box" message="No material shrinkage recorded" />}</div>
      </ReportCard>
    </div>
  );
}

export function ReorderCost() {
  const { data, loading, error } = useFetch('/api/reports/reorder-cost');
  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;
  return (
    <div>
      <div className="page-header"><h1>Reorder Cost Forecast</h1>
        <div className="page-actions">
          {data?.totalEstimatedCost > 0 && <span className="badge badge-amber">Est. total: {fmt.currency(data.totalEstimatedCost)}</span>}
        </div>
      </div>
      <div className="card">
        <div className="table-wrap"><table>
          <thead><tr><th>Material</th><th>On hand</th><th>Minimum</th><th>To order</th><th>Unit cost</th><th>Est. cost</th><th>Preferred vendor</th></tr></thead>
          <tbody>{(data?.items||[]).map((m,i) => (
            <tr key={i}>
              <td style={{ fontWeight:500 }}>{m.name}</td>
              <td>{m.quantity_on_hand} {m.unit_of_measure}</td>
              <td>{m.min_stock_level}</td>
              <td style={{ color:'var(--red)', fontWeight:500 }}>{m.qty_to_order}</td>
              <td>{fmt.currency(m.unit_cost)}</td>
              <td style={{ fontWeight:500 }}>{fmt.currency(m.estimated_cost)}</td>
              <td style={{ fontSize:12 }}>{m.preferred_vendor_name || '—'}</td>
            </tr>
          ))}</tbody>
        </table>{!(data?.items||[]).length && <EmptyState icon="ti-cash" message="All materials above minimum stock level" />}</div>
      </div>
    </div>
  );
}

export function WeeklyReport() {
  const { data, loading, error } = useFetch('/api/reports/weekly');
  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;
  return (
    <div>
      <div className="page-header"><h1>Weekly Inventory Report</h1><div className="page-actions"><span style={{ fontSize:12, color:'var(--text-tertiary)' }}>Generated {fmt.dateTime(data?.generatedAt)}</span></div></div>
      <div className="card">
        {data?.html ? <div dangerouslySetInnerHTML={{ __html: data.html }} /> : <EmptyState icon="ti-report" message="No report data" />}
      </div>
    </div>
  );
}

export function UpcomingDeliveries() {
  const [days, setDays] = React.useState(30);
  const { data, loading, error, refetch } = useFetch(`/api/purchase-orders/upcoming-deliveries?days=${days}`, [days]);

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} onRetry={refetch} />;

  const rows = data || [];
  const today = new Date();
  const soon = new Date(); soon.setDate(soon.getDate() + 7);

  return (
    <div>
      <div className="page-header">
        <h1>Upcoming Material Deliveries</h1>
        <div className="page-actions">
          <select value={days} onChange={e => setDays(parseInt(e.target.value))}>
            <option value={7}>Next 7 days</option>
            <option value={14}>Next 14 days</option>
            <option value={30}>Next 30 days</option>
            <option value={60}>Next 60 days</option>
            <option value={90}>Next 90 days</option>
          </select>
        </div>
      </div>
      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>PO #</th>
                <th>Vendor</th>
                <th>Status</th>
                <th>Issued</th>
                <th>Lead time</th>
                <th>Expected arrival</th>
                <th>Materials</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(po => {
                const arrival = new Date(po.expected_arrival_date);
                const isOverdue = arrival < today;
                const isSoon = !isOverdue && arrival <= soon;
                return (
                  <tr key={po.id}>
                    <td style={{ fontFamily:'monospace', fontSize:12 }}>{po.po_number}</td>
                    <td style={{ fontWeight:500 }}>{po.vendor_name}</td>
                    <td><span className="badge">{po.status}</span></td>
                    <td style={{ fontSize:12 }}>{po.issued_date ? fmt.date(po.issued_date) : '—'}</td>
                    <td style={{ fontSize:12 }}>{po.lead_time_days != null ? `${po.lead_time_days}d` : '—'}</td>
                    <td style={{ fontSize:12 }}>
                      <span style={{ color: isOverdue ? 'var(--color-danger)' : isSoon ? 'var(--color-warning)' : 'var(--color-success)', fontWeight:500 }}>
                        {isOverdue && <i className="ti ti-alert-triangle" style={{ marginRight:4 }} />}
                        {fmt.date(po.expected_arrival_date)}
                        {isOverdue && ' (overdue)'}
                      </span>
                    </td>
                    <td style={{ fontSize:12 }}>
                      {(po.items||[]).filter(i=>i.material_name).map((i,idx) => (
                        <div key={idx}>{i.material_name} — {fmt.number(i.quantity)} {i.unit_of_measure}</div>
                      ))}
                    </td>
                    <td>{fmt.currency(po.total_value)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!rows.length && <EmptyState icon="ti-truck" message={`No deliveries expected in the next ${days} days`} />}
        </div>
      </div>
    </div>
  );
}

export function InventoryOnHand() {
  const [vendorFeedOnly, setVendorFeedOnly] = useState(true);
  const { data, loading, error, refetch } = useFetch(
    `/api/reports/inventory-on-hand?vendorFeedOnly=${vendorFeedOnly}`
  );

  const STATUS_COLOR = {
    out_of_stock: { bg: 'var(--red-light, #fee2e2)', text: 'var(--red)', label: 'Out of stock' },
    low:          { bg: 'var(--amber-light, #fef3c7)', text: 'var(--amber)', label: 'Low' },
    ok:           { bg: 'transparent', text: 'var(--text-tertiary)', label: '' },
  };

  const [search, setSearch] = useState('');
  const products = (data?.products || []).filter(p =>
    !search ||
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.sku.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="page-header">
        <h1>Inventory On Hand</h1>
        <div className="page-actions">
          <button className="btn btn-sm" onClick={() =>
            downloadFile(`/api/reports/inventory-on-hand/csv?vendorFeedOnly=${vendorFeedOnly}`)
          }>
            <i className="ti ti-download" /> Export CSV
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="filter-bar" style={{ marginBottom: 12 }}>
        <div className="search-input">
          <i className="ti ti-search" />
          <input placeholder="Search SKU or name…" value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }}>
          <input type="checkbox" style={{ width:'auto' }}
            checked={vendorFeedOnly}
            onChange={e => { setVendorFeedOnly(e.target.checked); setSearch(''); }} />
          Vendor feed only
        </label>
      </div>

      {loading ? <Spinner /> : error ? <ErrorBanner message={error} onRetry={refetch} /> : (
        <>
          {/* Summary metrics */}
          <div className="metrics-grid" style={{ marginBottom: 16 }}>
            {[
              { label: 'Total SKUs', value: data?.totals?.totalSkus ?? 0 },
              { label: 'Units on hand', value: (data?.totals?.totalUnits ?? 0).toLocaleString() },
              { label: 'Inventory value', value: fmt.currency(data?.totals?.totalValue ?? 0), color: 'var(--blue)' },
              { label: 'Out of stock', value: data?.totals?.outOfStock ?? 0, color: data?.totals?.outOfStock > 0 ? 'var(--red)' : undefined },
              { label: 'Low stock', value: data?.totals?.lowStock ?? 0, color: data?.totals?.lowStock > 0 ? 'var(--amber)' : undefined },
            ].map((m, i) => (
              <div key={i} className="metric-card">
                <div className="metric-label">{m.label}</div>
                <div className="metric-value" style={m.color ? { color: m.color } : {}}>{m.value}</div>
              </div>
            ))}
          </div>

          {/* Product table */}
          <div className="card">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th style={{ textAlign:'right' }}>Qty on Hand</th>
                    <th>SKU</th>
                    <th style={{ textAlign:'right' }}>Unit Price</th>
                    <th style={{ textAlign:'center' }}>Feed</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, i) => {
                    const s = STATUS_COLOR[p.stock_status] || STATUS_COLOR.ok;
                    return (
                      <tr key={i} style={{ background: s.bg }}>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            {p.image_url
                              ? <img src={p.image_url} alt="" style={{ width:36, height:36, objectFit:'cover', borderRadius:4, flexShrink:0 }} />
                              : <div style={{ width:36, height:36, borderRadius:4, background:'var(--surface-2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                  <i className="ti ti-package" style={{ fontSize:14, color:'var(--text-tertiary)' }} />
                                </div>}
                            {p.name}{p.is_bundle ? <span style={{ fontSize:10, marginLeft:6, color:'var(--text-tertiary)' }}>bundle</span> : null}
                          </div>
                        </td>
                        <td style={{ textAlign:'right', fontWeight:600 }}>{parseInt(p.warehouse_qty || 0).toLocaleString()}</td>
                        <td style={{ fontFamily:'monospace', fontSize:12 }}>{p.sku}</td>
                        <td style={{ textAlign:'right' }}>{p.unit_price ? fmt.currency(p.unit_price) : '—'}</td>
                        <td style={{ textAlign:'center' }}>
                          {p.include_in_vendor_feed
                            ? <i className="ti ti-check" style={{ color:'var(--green)' }} />
                            : <span style={{ color:'var(--text-tertiary)' }}>—</span>}
                        </td>
                        <td>
                          {s.label && (
                            <span style={{ fontSize:11, padding:'2px 8px', borderRadius:4,
                              background: s.bg, color: s.text, fontWeight:600, border:`1px solid ${s.text}` }}>
                              {s.label}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background:'var(--surface-2)', fontWeight:600 }}>
                    <td colSpan={2} style={{ padding:'8px 12px' }}>Total ({products.length} SKUs)</td>
                    <td style={{ textAlign:'right', padding:'8px 12px' }}>
                      {products.reduce((s,p) => s + parseInt(p.warehouse_qty||0), 0).toLocaleString()}
                    </td>
                    <td colSpan={4}></td>
                  </tr>
                </tfoot>
              </table>
              {!products.length && <EmptyState icon="ti-package" message="No products found" />}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
